/*
 * The shared worker-state registry (ADR 0016): every state key a worker can compose, its
 * payload type, and its zod schema. Both sides use the same schema — the client encodes
 * snapshots/pushes onto the wire, the host decodes and validates before any participant is
 * touched. State payloads always ride the zod path regardless of the contract's request codec,
 * because keys like `session` carry types structured clone cannot round-trip (js-joda Instant).
 */
import { Instant } from '@js-joda/core';
import type { QueryCacheUserChangeEvent } from 'src/services/QueryCacheUserService';
import type { SessionState, TokenClaims } from 'src/services/SessionService';
import type { SessionServiceSignalSnapshot } from 'src/services/SessionService/ipcChannels';
import { z } from 'zod';

/**
 * The effective session snapshot delivered to workers: the signal snapshot the Electron IPC
 * federation already carries, extended with the superuser expiration both OptionsList and
 * Locates relay separately today.
 */
export interface WorkerSessionSnapshot extends SessionServiceSignalSnapshot {
    readonly superuserAccessEnabledUntil?: Instant;
}

/** The full evaluated feature-flag map, as held by the main-thread FeatureFlagService. */
export type FeatureFlagMap = Readonly<Record<string, unknown>>;

export interface WorkerStatePayloads {
    session: WorkerSessionSnapshot;
    featureFlags: FeatureFlagMap;
    user: QueryCacheUserChangeEvent;
}

export type WorkerStateKey = keyof WorkerStatePayloads;

export const WORKER_STATE_KEYS = ['session', 'featureFlags', 'user'] as const satisfies readonly WorkerStateKey[];

const tokenClaimsSchema = z.custom<TokenClaims>((value) => typeof value === 'object' && value !== null);

const sessionStateSchema = z.discriminatedUnion('phase', [
    z.object({ phase: z.literal('unauthenticated') }),
    z.object({ phase: z.literal('authenticating') }),
    z.object({ phase: z.literal('authenticated'), claims: tokenClaimsSchema }),
    z.object({ phase: z.literal('refreshing'), claims: tokenClaimsSchema }),
    z.object({
        phase: z.literal('expired'),
        reason: z.enum(['refresh-failed', 'server-rejected', 'logout']),
    }),
]) satisfies z.ZodType<SessionState>;

/** js-joda Instant is a class, so it crosses the wire as its ISO string. */
const instantCodec = z.codec(
    z.string(),
    z.custom<Instant>((value) => value instanceof Instant),
    {
        decode: (iso, payload) => {
            try {
                return Instant.parse(iso);
            } catch {
                payload.issues.push({ code: 'custom', message: `Invalid ISO instant: ${iso}`, input: iso });
                return z.NEVER;
            }
        },
        encode: (instant) => instant.toString(),
    },
);

export const workerSessionSnapshotSchema = z.object({
    state: sessionStateSchema,
    accessToken: z.string().nullable(),
    superuserAccessEnabledUntil: instantCodec.optional(),
});

export const queryCacheUserChangeEventSchema = z.object({
    kind: z.enum(['identity', 'entitlements']),
    effectiveUserId: z.string().optional(),
    fingerprint: z.string().optional(),
}) satisfies z.ZodType<QueryCacheUserChangeEvent>;

export type WorkerStateSchemas = {
    readonly [K in WorkerStateKey]: z.ZodType<WorkerStatePayloads[K]>;
};

export const workerStateSchemas: WorkerStateSchemas = {
    session: workerSessionSnapshotSchema,
    featureFlags: z.record(z.string(), z.unknown()),
    user: queryCacheUserChangeEventSchema,
};

export const isWorkerStateKey = (key: string): key is WorkerStateKey =>
    (WORKER_STATE_KEYS as readonly string[]).includes(key);

/** Encode a payload for the wire (client side: init snapshots and pushes). */
export const encodeWorkerStatePayload = <K extends WorkerStateKey>(
    key: K,
    payload: WorkerStatePayloads[K],
): unknown => {
    const schema: z.ZodType<WorkerStatePayloads[K]> = workerStateSchemas[key];
    return z.encode(schema, payload);
};

/** Decode and validate a wire payload (host side: before any participant is touched). Throws ZodError. */
export const decodeWorkerStatePayload = <K extends WorkerStateKey>(key: K, data: unknown): WorkerStatePayloads[K] => {
    const schema: z.ZodType<WorkerStatePayloads[K]> = workerStateSchemas[key];
    return z.decode(schema, data);
};

/**
 * Worker side — bound (multi, under the `WorkerStateParticipant` token) by each module that
 * consumes main-thread state.
 */
export interface WorkerStateParticipant<K extends WorkerStateKey = WorkerStateKey> {
    readonly key: K;
    /** Called with the init snapshot and for every subsequent push. Idempotent by construction. */
    apply(payload: WorkerStatePayloads[K]): void;
}

/** Main-thread side — a binding per key, resolved only for keys the manifest lists. */
export interface WorkerStateRelay<K extends WorkerStateKey = WorkerStateKey> {
    readonly key: K;
    /**
     * Synchronously emits the current value, then streams changes until disposed.
     *
     * Must be transactional: if the synchronous first emission throws, connect() must leave no
     * subscription behind — the Disposable was never returned, so nothing could ever clean one
     * up. (For a signal-`effect` implementation: dispose the effect when its first run throws.)
     */
    connect(send: (payload: WorkerStatePayloads[K]) => void): Disposable;
}
